/**
 * IronPact — module layer entry point.
 *
 * Runs after `public/legacy/core.js` (a classic script, so its API is already
 * on the global object) and before `DOMContentLoaded`, which is when the core
 * calls `initApp()`. That window is what lets the enhancement modules patch
 * behaviour cleanly instead of racing a timer, as the old inline scripts did.
 *
 * Layers, in load order:
 *   styles        — design tokens, base stylesheet, native shell overrides
 *   enhancements  — fixes and additions layered onto the legacy core
 *   features      — server-authoritative economy seams
 *   compliance    — the things the App Store and Play Store require
 *   native        — Capacitor shell: chrome, gestures, lifecycle, links
 *   pwa           — service worker and connectivity, web/PWA only
 *
 * See docs/ARCHITECTURE.md for how this is meant to be unwound over time.
 */

// No font imports. The app uses the platform's own type — SF Pro on iOS,
// Roboto on Android — which is what makes a wrapped web view read as an app,
// and which removes three bundled families from the binary. See tokens.css.

import './styles/tokens.css';
import './styles/app.css';
import './styles/native.css';

import { installTheme } from './enhancements/theme.js';
import { installCharts } from './enhancements/charts.js';
import { installIconography } from './enhancements/iconography.js';
import { installDashboardLayout } from './enhancements/dashboard-layout.js';
import { installMobileChrome } from './enhancements/mobile-chrome.js';
import { installMissionProgress } from './enhancements/mission-progress.js';
import { installAuthForms } from './enhancements/auth-forms.js';
import { installPasswordReset } from './enhancements/password-reset.js';
import { installAIPlans } from './enhancements/ai-plans.js';
import { installAlphaBanner } from './enhancements/alpha-banner.js';

import { installWorkoutRewards } from './features/entitlements/workout-rewards.js';
import { installMissionRewards } from './features/entitlements/mission-rewards.js';
import { installMysteryBox } from './features/entitlements/mystery-box.js';

import { installTodaySession } from './features/progression/today-card.js';
import { installSetPrefill } from './features/progression/set-prefill.js';

import { installLegalRoutes } from './compliance/legal-routes.js';
import { installPurchases } from './compliance/purchases.js';
import { installRevenueCat } from './compliance/revenuecat.js';

import { installNativeShell } from './native/index.js';
import { installServiceWorker } from './pwa/service-worker.js';
import { installConnectivity } from './pwa/connectivity.js';

import { isLegacyReady } from './runtime/legacy.js';
import { APP_VERSION } from './config/env.js';

function installEnhancements() {
  // Appearance and chart theming go first: both write to custom properties the
  // views below read as they render.
  installTheme();
  installCharts();
  installIconography();
  installDashboardLayout();
  installMobileChrome();
  installMissionProgress();
  installAuthForms();
  // Claims `#/reset-password` and rewrites a reset link's landing URL. Both
  // have to happen before `initApp()` routes for the first time, which running
  // here — ahead of DOMContentLoaded — guarantees.
  installPasswordReset();
  installAIPlans();
  installAlphaBanner();
}

function installEntitlements() {
  // Routes the post-workout payout through claim_workout_reward. Must be
  // installed before any workout can be finished, which `bootstrap()` running
  // ahead of DOMContentLoaded guarantees.
  installWorkoutRewards();
  installMissionRewards();
  installMysteryBox();
}

function installProgression() {
  // Both surfaces are additive DOM post-processing over views the legacy core
  // renders, so they are safe to install before any view exists.
  installTodaySession();
  installSetPrefill();
}

async function installCompliance() {
  // Account deletion lives in the legacy core (Profile → Delete Account),
  // wired to the `account-delete` edge function.
  installLegalRoutes();
  // Intercepts the Stripe checkout path on native before the billing provider
  // has a chance to register, so a tap during startup is blocked rather than
  // sent to a web checkout the stores forbid.
  installPurchases();
  // Awaited: registering the provider before the first paint means the buttons
  // are live by the time anyone can reach the Shop tab. A build with no
  // RevenueCat key returns immediately.
  await installRevenueCat();
}

async function bootstrap() {
  if (!isLegacyReady()) {
    // The core failed to evaluate — every module below assumes its globals.
    // Fail loudly here rather than in eight different places.
    console.error('[ironpact] legacy core did not load; module layer disabled');
    return;
  }

  installEnhancements();
  installEntitlements();
  installProgression();
  await installCompliance();

  await installNativeShell();

  installServiceWorker();
  await installConnectivity();

  console.info(`[ironpact] v${APP_VERSION} ready`);
}

bootstrap().catch((err) => {
  // A broken enhancement must not take the app down with it — the legacy core
  // is self-sufficient and will still render.
  console.error('[ironpact] bootstrap failed', err);
});
